/**
 * BusinessCarousel — v2.6.11
 *
 * Landing-page hero showcase. Renders on the LEFT half of the split hero
 * (desktop) or as a full-bleed rectangle at the top (mobile). Each slide
 * shows a REAL business photo as background with a slow Ken Burns zoom
 * effect that ALTERNATES direction on every other slide (zoom-in, zoom-out,
 * zoom-in, …). Dark overlay + industry label + benefit caption on top.
 *
 * v2.6.11 storyboard: electrician sending a job report, vending routeman
 * scanning a QR, dentist checking calendar, sales rep auto clock-in via
 * geofence, marketing person generating ads/images/videos, manager doing
 * KPI review. All 6 photos ship in /public/carousel/ — no external CDN.
 *
 * Content sources, in order of preference:
 *   1. Media Library assets tagged `landing_hero_carousel` (operator-supplied)
 *   2. Six built-in fallback slides with bundled PNGs + gradient fallback
 *      if any photo fails to load. Never renders empty.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Zap, Coffee, Stethoscope, MapPin, Sparkles, BarChart3,
  ChevronLeft, ChevronRight, Play,
} from 'lucide-react';
import SmartVideo from './SmartVideo';

const API = process.env.REACT_APP_BACKEND_URL;

// v2.6.11 — Hand-picked storyboard photos matching the exact narrative:
// electrician sending a job order, vending routeman scanning a QR code,
// dentist checking calendar, sales person auto-clocking in via geofence,
// marketing person generating ads/images/videos, manager doing KPI review.
// Each PNG is served straight from /carousel/ so the app is fully offline-
// capable and never depends on a third-party CDN.
const FALLBACK_SLIDES = [
  {
    id: 'electrician',
    icon: Zap,
    industry: 'Field Services',
    title: 'Electrician sends a job report',
    caption: 'From site to office — job order, photos, status in one tap.',
    from: 'from-sky-600', to: 'to-cyan-700',
    bgImage: '/carousel/electrician.png',
  },
  {
    id: 'vending',
    icon: Coffee,
    industry: 'Vending Operator',
    title: 'Routeman scans a coffee machine QR',
    caption: 'DEX pulled, stock counted, restock triggered.',
    from: 'from-emerald-600', to: 'to-teal-700',
    bgImage: '/carousel/vending_qr.png',
  },
  {
    id: 'dental',
    icon: Stethoscope,
    industry: 'Dental Clinic',
    title: 'Dentist checks today\'s bookings',
    caption: 'Calendar syncs with SMS confirmations automatically.',
    from: 'from-violet-600', to: 'to-purple-700',
    bgImage: '/carousel/dentist.png',
  },
  {
    id: 'geofence',
    icon: MapPin,
    industry: 'HR · Attendance',
    title: 'Sales rep steps into the office',
    caption: 'Geofence detects arrival — clocked in automatically at 08:57.',
    from: 'from-blue-600', to: 'to-indigo-700',
    bgImage: '/carousel/geofence.png',
  },
  {
    id: 'marketing',
    icon: Sparkles,
    industry: 'Marketing Studio',
    title: 'Marketer creates ads · images · videos',
    caption: 'One workspace — AI generates the full campaign in minutes.',
    from: 'from-fuchsia-600', to: 'to-purple-700',
    bgImage: '/carousel/marketing.png',
  },
  {
    id: 'kpi',
    icon: BarChart3,
    industry: 'People Ops',
    title: 'Manager runs a year-end KPI review',
    caption: 'Objective scores from live data — no more he-said-she-said.',
    from: 'from-amber-600', to: 'to-orange-700',
    bgImage: '/carousel/manager_kpi.png',
  },
];

export default function BusinessCarousel() {
  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [idx, setIdx] = useState(0);
  const [hovering, setHovering] = useState(false);
  const timerRef = useRef(null);

  // Try operator-uploaded carousel media first, else keep the fallbacks.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/media/public/landing-carousel`).then(res => {
      if (!alive) return;
      const items = res.data?.items || [];
      if (items.length >= 2) {
        setSlides(items.map(m => ({
          id: m.id,
          isMedia: true,
          url: m.external_url || m.file_url,
          mediaType: m.media_type,
          industry: m.description?.split('|')[0]?.trim() || '',
          title: m.title,
          caption: m.description?.split('|')[1]?.trim() || '',
        })));
      }
    }).catch(() => { /* fallback slides already loaded */ });
    return () => { alive = false; };
  }, []);

  // Auto-advance every 6s (bumped from 5s to give Ken Burns effect more breathing room)
  useEffect(() => {
    if (hovering) return;
    timerRef.current = setInterval(() => {
      setIdx(i => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, [hovering, slides.length]);

  const goTo = (i) => setIdx(((i % slides.length) + slides.length) % slides.length);

  return (
    <div
      className="relative w-full mx-auto overflow-hidden select-none
                 h-screen sm:h-auto
                 sm:aspect-square lg:aspect-square
                 lg:rounded-2xl lg:shadow-2xl lg:ring-1 lg:ring-black/10"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      data-testid="business-carousel"
    >
      {slides.map((s, i) => (
        <BusinessSlide key={s.id || i} slide={s} active={i === idx} index={i} />
      ))}

      {/* Prev / next arrows — visible on desktop hover, always visible on touch devices */}
      <button
        onClick={() => goTo(idx - 1)}
        aria-label="Previous slide"
        className={`absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/85 hover:bg-white text-gray-800 flex items-center justify-center shadow-lg transition-opacity duration-300 z-20
                   opacity-100 sm:${hovering ? 'opacity-100' : 'opacity-0'}`}
        data-testid="carousel-prev"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={() => goTo(idx + 1)}
        aria-label="Next slide"
        className={`absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/85 hover:bg-white text-gray-800 flex items-center justify-center shadow-lg transition-opacity duration-300 z-20
                   opacity-100 sm:${hovering ? 'opacity-100' : 'opacity-0'}`}
        data-testid="carousel-next"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Pagination dots — pushed up on mobile so browser bottom chrome doesn't clip them */}
      <div className="absolute bottom-10 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === idx ? 'w-8 bg-white shadow-md' : 'w-1.5 bg-white/50 hover:bg-white/75'}`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}


function BusinessSlide({ slide, active, index }) {
  const Icon = slide.icon;
  const [bgFailed, setBgFailed] = useState(false);

  // v2.5.925 — Alternate zoom direction. Even index = zoom-in (Ken Burns
  // slowly pushes toward the subject). Odd index = zoom-out (starts close
  // and slowly reveals more of the scene). Keeps the carousel feeling
  // alive without ever being distracting.
  const kbClass = active ? (index % 2 === 0 ? 'kb-zoom-in' : 'kb-zoom-out') : '';

  // Preload the bg image and set a failed flag if it 404s → gradient shows
  useEffect(() => {
    if (!slide.bgImage || slide.isMedia) return;
    const img = new Image();
    img.onerror = () => setBgFailed(true);
    img.src = slide.bgImage;
  }, [slide.bgImage, slide.isMedia]);

  return (
    <div
      className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
        active ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      aria-hidden={!active}
    >
      {slide.isMedia ? (
        /* User-uploaded media path */
        slide.mediaType === 'video' ? (
          <SmartVideo
            url={slide.url}
            title={slide.title}
            autoPlay={active}
            muted
            loop
            controls={false}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img
            src={slide.url?.startsWith('/') ? `${API}${slide.url}` : slide.url}
            alt={slide.title}
            className={`absolute inset-0 w-full h-full object-cover ${kbClass}`}
          />
        )
      ) : (
        <>
          {/* Colored gradient (base layer — always shows so we never render "empty") */}
          <div className={`absolute inset-0 bg-gradient-to-br ${slide.from} ${slide.to}`} />

          {/* Real photograph on top with Ken Burns zoom. If it 404s, gradient shows. */}
          {slide.bgImage && !bgFailed && (
            <div
              className={`absolute inset-0 bg-cover bg-center ${kbClass}`}
              style={{ backgroundImage: `url(${slide.bgImage})`, backgroundPosition: slide.bgFocus || 'center' }}
              aria-label={slide.title}
            />
          )}

          {/* Dark gradient overlay for legibility of overlaid text — tuned
              so the top 60% of the photo shines through and only the bottom
              (where the caption lives) is significantly darkened. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

          {/* Overlay content — bottom-aligned. On mobile, extra bottom padding
              so caption + pagination clear the browser navigation bar. */}
          <div className="absolute inset-0 flex flex-col justify-end pb-20 sm:pb-7 lg:pb-8 px-5 sm:px-7 lg:px-8 text-white">
            <div className="mb-3 h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center ring-1 ring-white/25 shadow-lg">
              <Icon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.75} />
            </div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-widest font-semibold text-white/85 mb-1.5">
              {slide.industry}
            </div>
            <h3 className="text-2xl sm:text-2xl lg:text-2xl xl:text-3xl font-bold leading-tight mb-1.5 max-w-md drop-shadow">
              {slide.title}
            </h3>
            <p className="text-sm sm:text-sm text-white/90 max-w-md leading-relaxed">
              {slide.caption}
            </p>
          </div>
        </>
      )}

      {/* Overlay caption for media slides (uploaded content still needs labels) */}
      {slide.isMedia && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent pointer-events-none" />
          <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-7 text-white pointer-events-none">
            {slide.industry && (
              <div className="text-[10px] sm:text-[11px] uppercase tracking-widest font-semibold text-white/85 mb-1.5">
                {slide.industry}
              </div>
            )}
            <h3 className="text-lg sm:text-2xl font-bold leading-tight drop-shadow">
              {slide.title}
              {slide.mediaType === 'video' && <Play className="inline h-4 w-4 ml-2 opacity-70" />}
            </h3>
            {slide.caption && (
              <p className="text-xs sm:text-sm text-white/90 mt-1.5 max-w-md leading-relaxed">{slide.caption}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
